import { useEffect, useState } from 'react';
import { SuperAdminModal } from '../../../baseComponents';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { editPractitionerAction } from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import * as Yup from 'yup';
import { getFromStorage } from 'src/lib/storage';
import './EditPractitioner.scss';

const EditPractitionerPage = ({
  isOpen,
  setIsOpen,
  selectedOrganizationUUID,
  setSelectedPractitioner,
}) => {
  const dispatch = useDispatch();
  const cachedUserEmail = getFromStorage(`user_email`);

  const { practitionerList } = useSelector((state) => state?.practitionerState);
  const { getAllConditionsList } = useSelector(
    (state) => state?.conditionState
  );
  const { getPractitionerById } = useSelector((state) => state?.doctorsState);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);

  const { getOnboardingSpecialties, getOnboardingRoles } = useSelector(
    (state) => state?.onboardingState
  );

  useEffect(() => {
    if (
      getOnboardingSpecialties &&
      Array.isArray(getOnboardingSpecialties) &&
      getOnboardingSpecialties.length > 0
    ) {
      const dataArr = getOnboardingSpecialties.map((specialty, index) => ({
        key: `${specialty.specialty_id}_${index}`,
        value: specialty.specialty_id,
        label: specialty.specialty_name ? specialty.specialty_name : 'N/A',
      }));
      setSpecialtyOptions(dataArr);
    }
  }, [getOnboardingSpecialties]);

  useEffect(() => {
    if (
      getOnboardingRoles &&
      Array.isArray(getOnboardingRoles) &&
      getOnboardingRoles.length > 0
    ) {
      const dataArr = getOnboardingRoles.map((roles, index) => ({
        key: `${roles.role_id}_${index}`,
        value: roles.role_id,
        label: roles.role_name ? roles.role_name : 'N/A',
      }));
      setRoleOptions(dataArr);
    }
  }, [getOnboardingRoles]);

  useEffect(() => {
    if (practitionerList && Array.isArray(practitionerList)) {
      let dataArr = [];
      practitionerList.forEach((key) => {
        dataArr.push({
          value: key?.practitioner_id,
          label: key?.full_name,
        });
      });
    }
  }, [practitionerList]);

  useEffect(() => {
    if (
      getAllConditionsList &&
      Array.isArray(getAllConditionsList) &&
      getAllConditionsList.length > 0
    ) {
      const dataArr = getAllConditionsList.map((condition, index) => ({
        key: `${condition.condition_id}_${index}`,
        value: condition.condition_id,
        label: `${condition.specialty_name} - ${condition.condition_name ? condition.condition_name : 'N/A'}`,
      }));
      setConditionOptions(dataArr);
    }
  }, [getAllConditionsList]);

  const initialValues = {
    practitioner_id:
      getPractitionerById?.Practitioner_conditions_details?.length > 0 &&
      getPractitionerById?.Practitioner_conditions_details[0]
        ?.practitioner_name,
    condition_name:
      getPractitionerById?.Practitioner_conditions_details &&
      getPractitionerById?.Practitioner_conditions_details[0]?.condition_id &&
      getPractitionerById?.Practitioner_conditions_details?.length > 0
        ? [
            ...new Set(
              getPractitionerById?.Practitioner_conditions_details.map(
                (condition) => condition.condition_id
              )
            ),
          ]
        : [],
    org_id: getPractitionerById?.Organization_details?.org_name,
    get_role: getPractitionerById?.role_id
      ? getPractitionerById?.role_id
      : null,
    get_primary_specialty: getPractitionerById?.specialty_id
      ? getPractitionerById?.specialty_id
      : null,
  };

  const validationSchemaField = Yup.object().shape({
    practitioner_id: Yup.string().required('Doctor name is required'),
    condition_name: Yup.array().min(1, 'Condition is required'),
    get_primary_specialty: Yup.string().required(
      'Primary specialty is required'
    ),
  });

  const sections = [
    {
      heading: 'Edit user',
      fields: [],
    },
    {
      className: 'flex gap-6 item-center',
      fields: [
        {
          name: 'org_id',
          type: 'custom',
          className: 'font-normal text-sm items-center justify-center',
          disabled: true,
          render: () => (
            <span className="justify-center">
              <b>Organization name :</b> {initialValues.org_id}
            </span>
          ),
        },
        {
          name: 'practitioner_id',
          type: 'custom',
          placeholder: 'Select practitioner',
          disabled: true,
          className: ' font-normal text-sm items-center justify-center',
          render: () => (
            <span className="justify-center">
              <b>Practitioner name :</b> {initialValues.practitioner_id}
            </span>
          ),
        },
      ],
    },
    {
      className: 'grid grid-cols-2 gap-4',
      fields: [
        {
          name: 'get_role',
          type: 'select',
          title: 'Role',
          placeholder: 'Select role',
          inputClassName: 'overflow-auto flex items-center grid grid-cols-0',
          options: roleOptions,
          onChange: (setFieldValue, e) => {
            setFieldValue('get_role', e);
          },
        },
        {
          name: 'get_primary_specialty',
          type: 'select',
          title: 'Primary specialty',
          placeholder: 'Select primary specialty',
          options: specialtyOptions,
          onChange: (setFieldValue, e) => {
            setFieldValue('get_primary_specialty', e);
          },
        },
      ],
    },
    {
      className: '',
      fields: [
        {
          name: 'condition_name',
          type: 'select',
          title: 'Condition name',
          placeholder: 'Select condition',
          options: conditionOptions,
          dropdownMode: 'multiple',
          onChange: (setFieldValue, e) => {
            setFieldValue('condition_name', e);
          },
        },
      ],
    },
  ];

  return (
    <SuperAdminModal
      open={isOpen}
      sections={sections}
      initialValues={initialValues}
      validationSchema={validationSchemaField}
      closable={true}
      submitButtonText="Submit"
      cancelButtonText="Cancel"
      className="edit-practitioner-modal SuperAdmin"
      centered={true}
      handleCancel={() => {
        setIsOpen(false);
      }}
      handleSubmit={(values) => {
        const payload = {
          practitioner_id:
            getPractitionerById?.Practitioner_conditions_details[0]
              ?.practitioner_id,
          condition_ids: values?.condition_name,
          last_modified_by: cachedUserEmail,
          primary_specialty_id: values?.get_primary_specialty,
          role_id: values?.get_role,
        };
        dispatch(
          editPractitionerAction(
            payload,
            setIsOpen,
            selectedOrganizationUUID,
            setSelectedPractitioner
          )
        );
      }}
    />
  );
};

export default EditPractitionerPage;
